import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CreateTenantJobDto } from '@app/Models/create-tenant-job.dto';
import { CreateTenantTrial } from '@app/Models/create-tenant-trial.model';
import { NewCloneTenantDto } from '@app/Models/new-clone-tenant.dto';
import { NewFreeTrialTenantDto } from '@app/Models/new-free-trial-tenant.dto';
import { Tenant } from '@app/Models/tenant';
import { TenantService } from '@app/Services';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class CloneService {
    cloneInProgress$ = new BehaviorSubject(false);
    cloneFinalised$ = new BehaviorSubject(false);
    cloneJobId$ = new BehaviorSubject<string | null>(null);
    sourceTenant$ = new BehaviorSubject<Tenant | null>(null);
    newTenant$ = new BehaviorSubject<Tenant | null>(null);
    environmentId$ = new BehaviorSubject<number | null>(null);

    constructor(private tenantService: TenantService, private http: HttpClient) { }

    hasCloneStarted(): Observable<boolean> {
        return combineLatest([
            this.cloneInProgress$.asObservable(),
            this.cloneFinalised$.asObservable()]
        ).pipe(
            map(([cloneInProgress, cloneFinalised]) =>
                cloneInProgress || cloneFinalised
            )
        );
    }

    reset(): void {
        this.cloneInProgress$.next(false);
        this.cloneFinalised$.next(false);
        this.sourceTenant$.next(null);
        this.newTenant$.next(null);
    }

    getCloneJobAndTenantId(): Observable<CreateTenantJobDto> {
        return this.tenantService.getCloneJobAndTenantId().pipe(
            filter(jobAndTenantId => !!jobAndTenantId),
            tap((jobAndTenantId) => {
                this.cloneJobId$.next(jobAndTenantId.JobId);
            })
        );
    }

    getNewTenant(): Tenant {
        return this.newTenant$.value;
    }

    cloneExportImport(sourceTenant: Tenant, sourceEnvironmentId: number, newTenant: Tenant, cloneRecords: boolean): Observable<string> {
        const newCloneTenant: NewCloneTenantDto = {
            NewTenant: newTenant,
            EnvironmentId: sourceEnvironmentId,
            ExistingTenantName: sourceTenant.Value,
            CloneRecords: cloneRecords,
        } as NewCloneTenantDto;

        return this.tenantService.cloneTenantExportImport(newCloneTenant).pipe(
            tap((jobId) => {
                this.cloneInProgress$.next(true);
                this.cloneJobId$.next(jobId);
                this.newTenant$.next(newTenant);
                this.sourceTenant$.next(sourceTenant);
            })
        );
    }

    cloneFreeTrial(sourceTenant: Tenant, sourceEnvironmentId: number, trial: CreateTenantTrial): Observable<string> {
        const newFreeTrialTenant: NewFreeTrialTenantDto = {
            Trial: trial,
            EnvironmentId: sourceEnvironmentId,
            ExistingTenantName: sourceTenant.Value,
        } as NewFreeTrialTenantDto;

        return this.tenantService.cloneTenantFreeTrial(newFreeTrialTenant).pipe(
            tap((jobId) => {
                this.cloneInProgress$.next(true);
                this.cloneJobId$.next(jobId);
                this.sourceTenant$.next(sourceTenant);
            })
        );
    }

    cloneExportOnlyRequest(sourceTenant: Tenant, sourceEnvironmentId: number, cloneRecords: boolean): Observable<string> {
        const newCloneTenant: NewCloneTenantDto = {
            EnvironmentId: sourceEnvironmentId,
            ExistingTenantName: sourceTenant.Value,
            CloneRecords: cloneRecords,
        } as NewCloneTenantDto;

        return this.tenantService.cloneTenantExportOnly(newCloneTenant).pipe(
            tap((jobId) => {
                this.sourceTenant$.next(sourceTenant);
                this.cloneJobId$.next(jobId);
                this.cloneInProgress$.next(true);
            })
        );
    }

    cloneImportOnlyRequest(newTenant: Tenant, importId: string): Observable<string> {
        this.cloneInProgress$.next(true);

        const newCloneTenant: NewCloneTenantDto = {
            NewTenant: newTenant,
            ImportId: importId
        } as NewCloneTenantDto;

        return this.tenantService.cloneTenantImportOnly(newCloneTenant).pipe(
            tap((jobId) => {
                this.cloneJobId$.next(jobId);
                this.newTenant$.next(newTenant);
            })
        );
    }

    checkImportIdExists(importId: string): Observable<boolean> {
        const url = `/api/tenant/clone/import-id/validate/${importId}`;
        return this.http.get<boolean>(url).pipe(
            catchError(() => of(false))
        );
    }
}
